import {announce} from '@github-ui/aria-live'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {fuzzyFilter} from '@github-ui/fuzzy-score/fuzzy-filter'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {BaseItemPickerProps} from '@github-ui/item-picker-base/base-item-picker-props'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {LABELS, SUGGESTION_REASON, SUGGESTION_REASON_TEXT} from '@github-ui/item-picker-base/constants/labels'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import {URLS} from '@github-ui/item-picker-base/constants/urls'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import type {ItemGroup} from '@github-ui/item-picker-base/shared'
import {isLabelArchived} from '@github-ui/label-token/RepositoryLabelToken'
import {
  InlineLabelForm,
  type InlineLabelFormLabel,
  InlineLabelFormVariant,
} from '@github-ui/repository-label/InlineLabelForm'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {useDebounce} from '@github-ui/use-debounce'
import {ArchiveIcon, ArrowLeftIcon, PlusIcon} from '@primer/octicons-react'
import {IconButton, SelectPanel, type SelectPanelItemProps as ItemProps} from '@primer/react'
import type React from 'react'
import {useCallback, useDeferredValue, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'

import styles from './LabelPickerBase.module.css'

export type LabelPickerLabel = {
  id: string
  databaseId?: number | null
  color: string
  name: string
  nameHTML: string
  description: string | null | undefined
  url: string
  /**
   * Set when the label has been archived. Optional because not every producer
   * of this shape selects the field.
   */
  archivedAt?: string | null
  /**
   * The sharding-safe (int64-as-string) label id. Optional because not every
   * producer of this shape selects the field.
   */
  fullDatabaseId?: string | null
}

/**
 * A single server-ranked label suggestion.
 *
 * The server owns ordering, precedence and the cap: the list arrives already
 * merged across its sources, deduplicated, and truncated. The client renders it
 * as-is and only owns the display copy for `reason`.
 */
export type LabelSuggestion = {
  label: LabelPickerLabel
  /**
   * Stable machine-readable attribution for why this label was suggested
   * ("user_frequent", "popular"). Deliberately typed as a plain string: the
   * server may introduce new sources ahead of the client, and an unrecognized
   * reason must degrade to "no subtext" rather than break the group.
   */
  reason: string
  /** 0-based rank *within* the reason's group, not globally. */
  rank: number
}

export type LabelPickerBaseProps = Omit<BaseItemPickerProps<LabelPickerLabel>, 'filterItems'> & {
  repo: string
  owner: string
  labels: LabelPickerLabel[]
  canCreateLabel: boolean
  suggestions?: LabelPickerLabel[]
  copilotSuggestedLabels?: LabelPickerLabel[]
  /**
   * Server-ranked suggestions rendered as the top "Suggestions" group, in the
   * order given. Only consulted when `enableSuggestedLabels` is true.
   */
  suggestedLabels?: LabelSuggestion[]
  /**
   * When true (the server-gated "suggested_labels" experience is on), the
   * server-ranked suggestions render as a single "Suggestions" group above the
   * label list, each with a reason subtext. When false, no suggestions group is
   * rendered and the picker keeps its original layout.
   */
  enableSuggestedLabels?: boolean
  noLabelOption?: ExtendedItemProps<LabelPickerLabel>
  initialFilter?: string
  showEditLabelsButton?: boolean
  showNoMatchItem?: boolean
  showColors?: boolean
  /**
   * Called on each false -> true transition of the panel. Consumers use this to
   * snapshot async data (e.g. suggestions) for the duration of an open, so a
   * background revalidation can't reorder the list under the user's cursor.
   *
   * Not called for the initial open when the picker mounts already open via
   * `triggerOpen`.
   */
  onPickerOpen?: () => void
  /** Called when the panel closes. See `onPickerOpen`. */
  onPickerClose?: () => void
  /** Called after a label is created through the inline form. */
  onLabelCreated?: (label: LabelPickerLabel) => void
}

export type LabelPickerInternalProps = {
  totalNumberOfLabels: number
  searchLabels?: (query: string) => Promise<LabelPickerLabel[]>
  canCreateLabel: boolean
  repositoryId?: string
  setIsLoading?: (isLoading: boolean) => void
  onCreate?: CreateLabelMutationCallback
}

export type MutationCallback<TVariables, TResponse> = (
  variables: TVariables,
  options: {onSuccess: (response: TResponse | null) => void; onError: () => void},
) => void
export type CreateLabelVariables = {repositoryId: string; color: string; name: string}
export type CreateLabelMutationCallback = MutationCallback<CreateLabelVariables, LabelPickerLabel>

const DEFAULT_SUGGESTIONS: LabelPickerLabel[] = []
const DEFAULT_SUGGESTED_LABELS: LabelSuggestion[] = []
const FILTERED_RESULTS_LIMIT = 30
// Defensive cap on the "Suggestions" group. The server already caps the list it
// returns and owns the real limit; this only guards the picker against a server
// (or a stale cache entry) handing back more than the layout was designed for.
const SUGGESTED_LABELS_LIMIT = 5

// Attributes a selected label to the source group it was actually shown in.
// Only labels that actually made it into the rendered (capped) suggestions group
// report a server reason; overflow labels render under "Labels" and fall back to
// `searchResult`.
function resolveSuggestionReason(
  label: LabelPickerLabel,
  {
    suggestionsById,
    copilotSuggestedLabels,
  }: {
    suggestionsById: Map<string, LabelSuggestion>
    copilotSuggestedLabels?: LabelPickerLabel[]
  },
): string {
  const suggestion = suggestionsById.get(label.id)
  if (suggestion) return suggestion.reason

  if ((copilotSuggestedLabels ?? []).some(l => l.id === label.id)) return SUGGESTION_REASON.copilot

  return SUGGESTION_REASON.searchResult
}

const labelsGroup: ItemGroup = {groupId: 'labels', header: {title: 'Selected labels', variant: 'filled'}}
// When the suggested-labels experience is enabled, the selected group is titled
// simply "Selected" to sit cleanly above the "Suggestions"/"Labels" groups.
const selectedGroup: ItemGroup = {groupId: 'labels', header: {title: 'Selected', variant: 'filled'}}
const suggestedGroup: ItemGroup = {groupId: 'suggested', header: {title: 'Suggestions', variant: 'filled'}}
const suggestionsGroup: ItemGroup = {groupId: 'suggestions', header: {title: 'Suggestions', variant: 'filled'}}
// When the suggested-labels experience is enabled, the top "Suggestions" group
// holds favorites + popular, so the normal unselected list is relabeled "Labels"
// to disambiguate. Consumers with the feature off keep the original "Suggestions".
const labelsListGroup: ItemGroup = {groupId: 'suggestions', header: {title: 'Labels', variant: 'filled'}}
const copilotGroup: ItemGroup = {groupId: 'copilot', header: {title: 'Copilot suggestions', variant: 'filled'}}
type CreateLabelEntryPoint = 'no_match_action'
type CreateLabelAnalyticsAction = 'initiated' | 'abandoned' | 'completed'
type CreateLabelActionItem = LabelPickerLabel & {__isCreateAction__: true}
type LabelPickerItemActionEvent = React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>

function createLabelActionItemProps({
  label,
  inlineLabelCreationEnabled,
  repositoryId,
  openCreateLabelForm,
  createNewLabel,
}: {
  label: LabelPickerLabel
  inlineLabelCreationEnabled: boolean
  repositoryId?: string
  openCreateLabelForm: (entryPoint: CreateLabelEntryPoint, initialName?: string) => void
  createNewLabel: (item: ItemProps, label: LabelPickerLabel, event: LabelPickerItemActionEvent) => void
}): ExtendedItemProps<LabelPickerLabel> {
  const itemProps: ExtendedItemProps<LabelPickerLabel> = {
    id: label.id,
    description: label.description || undefined,
    descriptionVariant: 'block',
    source: label,
    text: LABELS.createLabel(label.name),
    trailingVisual: PlusIcon,
    className: styles.labelPickerItem,
    onAction: (item: ItemProps, event: LabelPickerItemActionEvent) => {
      if (inlineLabelCreationEnabled && repositoryId) {
        // ItemPicker stores this object in its selection cache and toggles
        // it before invoking the action.
        itemProps.selected = false
        event.preventDefault()
        event.stopPropagation()
        openCreateLabelForm('no_match_action', label.name)
        return
      }

      createNewLabel(item, label, event)
    },
  }

  return itemProps
}

export function LabelPickerBase({
  labels: selectedLabels,
  suggestions = DEFAULT_SUGGESTIONS,
  totalNumberOfLabels,
  copilotSuggestedLabels,
  suggestedLabels = DEFAULT_SUGGESTED_LABELS,
  enableSuggestedLabels = false,
  searchLabels,
  onSelectionChange,
  insidePortal,
  anchorElement,
  nested = false,
  triggerOpen = true,
  initialFilter,
  owner,
  repo,
  showEditLabelsButton = true,
  noLabelOption = undefined,
  title,
  showNoMatchItem = false,
  showColors = true,
  canCreateLabel,
  repositoryId,
  isLoading = false,
  setIsLoading,
  onCreate,
  anchorSide,
  onPickerOpen,
  onPickerClose,
  onLabelCreated,
}: LabelPickerBaseProps & LabelPickerInternalProps) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const removeLabelsLoadingEnabled = isFeatureEnabled('issues_react_remove_labels_loading')
  const responsiveOnNarrowEnabled = isFeatureEnabled('issues_react_select_panel_fullscreen_on_narrow')
  const inlineLabelCreationEnabled = isFeatureEnabled('issues_inline_label_creation')
  const [filter, setFilter] = useState('')
  // Defer filter updates to keep input responsive during expensive list reconciliation
  const deferredFilterValue = useDeferredValue(filter)
  // The picker's item props (and their onAction handlers) are cached by the base
  // ItemPicker, so a handler can close over a stale `filter`. Mirror the latest
  // committed value into a ref and read it at selection time for accurate
  // "did the user search first?" telemetry.
  const filterRef = useRef(filter)
  useEffect(() => {
    filterRef.current = filter
  }, [filter])
  const filterTermCacheRef = useRef<Set<string>>(new Set())
  const [isServerFetching, setIsServerFetching] = useState(false)
  const [loadedLabels, setLoadedLabels] = useState<Array<LabelPickerLabel & {__isNew__?: boolean}>>(suggestions)
  const [createdLabels, setCreatedLabels] = useState<Array<LabelPickerLabel & {__isNew__?: boolean}>>([])
  const [createdLabelsForSelection, setCreatedLabelsForSelection] = useState<
    Array<LabelPickerLabel & {__isNew__?: boolean}>
  >([])
  const [isCreateLabelFormOpen, setIsCreateLabelFormOpen] = useState(false)
  const [isCreateLabelPending, setIsCreateLabelPending] = useState(false)
  const [createLabelInitialName, setCreateLabelInitialName] = useState('')
  const anchorRef = useRef<HTMLButtonElement>(null)
  const filterInputRef = useRef<HTMLInputElement>(null)
  const createLabelReturnFocusRef = useRef<HTMLElement | null>(null)
  const activeCreateLabelEntryPointRef = useRef<CreateLabelEntryPoint | null>(null)
  // Tracks the ids currently toggled on inside the open picker so the
  // suggestion-selection analytics event fires only for newly *added* labels
  // (not removals). Seeded from the already-applied labels.
  const liveSelectedIdsRef = useRef<Set<string>>(new Set(selectedLabels.map(l => l.id)))
  useEffect(() => {
    const ref = liveSelectedIdsRef.current
    return () => ref.clear()
  }, [])
  const {addToast} = useToastContext()
  const repoNwo = `${owner}/${repo}`

  // Synchronize loadedLabels with suggestions when they change.
  //
  // Deferred while a filter is active: `loadedLabels` then holds server search
  // results for labels outside the initial page, and `suggestions` changes
  // identity for reasons unrelated to the search (a late-arriving suggestions
  // response, a revalidation of the label list). Overwriting would drop those
  // results while the search input still shows the term that found them, and
  // re-entering the same term does not re-issue the search.
  //
  // A deferred pool is not lost: the effect runs on `filter` as well, so the
  // newest `suggestions` lands as soon as the filter clears. Under
  // `issues_react_remove_labels_loading` the unfiltered picker renders
  // `loadedLabels`, so dropping the update outright would leave that list stale
  // for the life of the picker. Comparing identities keeps a cleared filter from
  // resetting a pool that is already current.
  const syncedSuggestionsRef = useRef(suggestions)
  const syncLoadedLabels = useCallback(() => {
    if (syncedSuggestionsRef.current === suggestions) return
    syncedSuggestionsRef.current = suggestions
    // The cache records which terms are already merged into `loadedLabels`, so it
    // cannot outlive the list it describes.
    filterTermCacheRef.current.clear()
    setLoadedLabels(suggestions)
  }, [suggestions])
  useEffect(() => {
    if (filter) return
    syncLoadedLabels()
  }, [filter, syncLoadedLabels])

  const shouldServerFetch = searchLabels && totalNumberOfLabels > suggestions.length

  const sortedSelectedLabels = useMemo(
    () => selectedLabels.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [selectedLabels],
  )

  // Memoize key functions to avoid recreating them on every render
  const getLabelName = useCallback((l: LabelPickerLabel) => l.name, [])
  const getLabelNameAndDescription = useCallback((l: LabelPickerLabel) => `${l.name} ${l.description ?? ''}`, [])

  // The rendered "Suggestions" group, straight off the server's ordering.
  //
  // The server owns ranking, cross-source precedence and deduplication, so there
  // is nothing to merge here: take its order and apply a defensive cap. Each
  // entry carries its own fully serialized label, so a suggestion does not have
  // to appear in the loaded label pool to be shown.
  //
  // Selection is intentionally NOT considered when claiming these slots: the top
  // SUGGESTED_LABELS_LIMIT ranked labels are locked in regardless of what's
  // selected. Selecting a suggested label then just removes it from the group at
  // render time (it moves to "Selected") without backfilling a replacement, so
  // the visible suggestions shrink instead of pulling in the next-ranked label.
  const suggestionEntries = useMemo(() => {
    if (!enableSuggestedLabels) return DEFAULT_SUGGESTED_LABELS
    return suggestedLabels.slice(0, SUGGESTED_LABELS_LIMIT)
  }, [enableSuggestedLabels, suggestedLabels])

  // Membership and reason lookup for a rendered label. Resolved to a Map up
  // front so per-label classification (in `items`, `resolveGroupId` and
  // `convertToItemProps`) stays O(1) on a hot path that can hold ~1000 labels.
  const suggestionsById = useMemo(
    () => new Map(suggestionEntries.map(entry => [entry.label.id, entry] as const)),
    [suggestionEntries],
  )

  const lookupSets = useMemo(
    () => ({
      selectedIds: new Set(selectedLabels.map(l => l.id)),
      copilotIds: new Set((copilotSuggestedLabels ?? []).map(l => l.id)),
    }),
    [selectedLabels, copilotSuggestedLabels],
  )

  const items = useMemo(() => {
    if (!deferredFilterValue) {
      const relevantLabels = removeLabelsLoadingEnabled
        ? loadedLabels
        : [...createdLabels, ...suggestions.filter(label => !createdLabels.some(created => created.id === label.id))]
      const {selectedIds} = lookupSets

      // First, collect all the labels that are already selected
      const allLabels = [...sortedSelectedLabels]

      // Server-ranked suggestions, rendered in the order the server returned.
      // Selected labels are dropped here (they render in the "Selected" group)
      // without freeing their slot, so the group shrinks rather than backfilling.
      const suggestedLabelsToRender = suggestionEntries
        .map(entry => entry.label)
        .filter(label => !selectedIds.has(label.id))
      const suggestedIds = new Set(suggestedLabelsToRender.map(l => l.id))

      // Then add unselected copilot labels that aren't already surfaced above
      const unselectedCopilotLabels = (copilotSuggestedLabels ?? []).filter(
        l => !selectedIds.has(l.id) && !suggestedIds.has(l.id),
      )
      const unselectedCopilotIds = new Set(unselectedCopilotLabels.map(l => l.id))

      // Then add unselected regular labels that aren't already surfaced above.
      // Suggested labels beyond the cap fall through to here and render as
      // normal labels.
      const unselectedRegularLabels = relevantLabels.filter(
        l => !selectedIds.has(l.id) && !suggestedIds.has(l.id) && !unselectedCopilotIds.has(l.id),
      )

      // Combine all labels (server-ranked suggestions first)
      allLabels.push(...suggestedLabelsToRender, ...unselectedCopilotLabels, ...unselectedRegularLabels)

      // If we render the picker with all the labels we loaded it will cause some serious jank.
      // Instead, only render a sensible page size of labels.
      return allLabels.slice(0, VALUES.labelsInitialLoadCount)
    }

    // For filtered results, include loaded labels and every source that can
    // surface labels outside that pool.
    const uniqueItemsMap = new Map()

    // Add loaded labels first
    for (const label of loadedLabels) {
      uniqueItemsMap.set(label.id, label)
    }
    // Server-ranked suggestions carry fully serialized labels and may not be
    // present in the loaded page, so keep them searchable after the user types.
    for (const {label} of suggestionEntries) {
      if (!uniqueItemsMap.has(label.id)) {
        uniqueItemsMap.set(label.id, label)
      }
    }
    // Add copilot labels, but only if they're not already in the map
    for (const label of copilotSuggestedLabels ?? []) {
      if (!uniqueItemsMap.has(label.id)) {
        uniqueItemsMap.set(label.id, label)
      }
    }

    const itemsToFilter = Array.from(uniqueItemsMap.values())

    const createdLabelIdsForSelection = new Set(createdLabelsForSelection.map(label => label.id))
    const filteredItems = fuzzyFilter<LabelPickerLabel>({
      items: itemsToFilter,
      filter: deferredFilterValue,
      key: getLabelName,
      secondaryKey: getLabelNameAndDescription,
    }).filter(label => !createdLabelIdsForSelection.has(label.id))

    return [...createdLabelsForSelection, ...filteredItems].slice(0, FILTERED_RESULTS_LIMIT)
  }, [
    deferredFilterValue,
    loadedLabels,
    createdLabels,
    createdLabelsForSelection,
    removeLabelsLoadingEnabled,
    suggestions,
    sortedSelectedLabels,
    copilotSuggestedLabels,
    suggestionEntries,
    lookupSets,
    getLabelName,
    getLabelNameAndDescription,
  ])

  const fetchSearchData = useCallback(
    async (query: string) => {
      if (!removeLabelsLoadingEnabled && query === '') {
        setLoadedLabels(suggestions)
        setIsServerFetching(false)
        return
      }

      if (removeLabelsLoadingEnabled && filterTermCacheRef.current.has(query)) {
        setIsServerFetching(false)
        return
      }

      if (!searchLabels) return
      setIsServerFetching(true)

      try {
        const results = await searchLabels(query)
        // Functional update: a concurrent resync or an overlapping search may have
        // written between this call and its response, and reading `loadedLabels`
        // from the closure would drop that write.
        setLoadedLabels(prev => (removeLabelsLoadingEnabled ? uniqById([...prev, ...results]) : results))
        // Recorded only on success. Caching a failed term would suppress every
        // later attempt at it without ever having merged its results.
        if (removeLabelsLoadingEnabled) {
          filterTermCacheRef.current.add(query)
        }
      } catch {
        // Temporarily disabling this until we have a standard way of displaying fetch errors
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotSearchLabels,
        })
      } finally {
        setIsServerFetching(false)
      }
    },
    [removeLabelsLoadingEnabled, searchLabels, suggestions, addToast],
  )

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => fetchSearchData(nextValue),
    VALUES.pickerDebounceTime,
  )

  const filterItems = useCallback(
    (value: string) => {
      const trimmedFilter = value.trim()
      if (filter !== value) {
        setCreatedLabelsForSelection(currentLabels => (currentLabels.length === 0 ? currentLabels : []))
      }
      if (filter !== trimmedFilter && shouldServerFetch) {
        setIsServerFetching(true)
        debounceFetchSearchData(trimmedFilter)
        setFilter(value)
        return
      }
      if (suggestions.length !== 0 || !isLoading) setFilter(value)
    },
    [debounceFetchSearchData, filter, isLoading, shouldServerFetch, suggestions.length],
  )

  const getItemKey = useCallback((label: LabelPickerLabel) => label.id, [])

  const emitCreateLabelAnalytics = useCallback(
    (action: CreateLabelAnalyticsAction, entryPoint: CreateLabelEntryPoint) => {
      sendClickAnalyticsEvent({
        category: 'LABEL_PICKER',
        action: `label_creation:${action}`,
        label: `ref_cta:create_label;ref_loc:${entryPoint};`,
      })
    },
    [sendClickAnalyticsEvent],
  )

  const openCreateLabelForm = useCallback(
    (
      entryPoint: CreateLabelEntryPoint,
      initialName = '',
      returnFocusElement: HTMLElement | null = filterInputRef.current,
    ) => {
      activeCreateLabelEntryPointRef.current = entryPoint
      emitCreateLabelAnalytics('initiated', entryPoint)
      createLabelReturnFocusRef.current = returnFocusElement
      setCreateLabelInitialName(initialName)
      setIsCreateLabelFormOpen(true)
    },
    [emitCreateLabelAnalytics],
  )

  const createNewLabel = useCallback(
    (
      _: ItemProps,
      label: LabelPickerLabel,
      event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
    ) => {
      event.preventDefault()
      event.stopPropagation()

      if (!repositoryId || !onCreate || !setIsLoading) return
      setIsLoading(true)

      onCreate(
        {
          repositoryId,
          color: label.color,
          name: label.name,
        },
        {
          onError: () => {
            // Temporarily disabling this until we have a standard way of displaying fetch errors
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({
              type: 'error',
              message: ERRORS.couldNotCreateLabel,
            })
            setIsLoading(false)
          },
          onSuccess: response => {
            if (response) {
              setLoadedLabels([{...response, __isNew__: true}, ...loadedLabels])
            }
            setIsLoading(false)
          },
        },
      )
    },
    [addToast, loadedLabels, onCreate, repositoryId, setIsLoading],
  )

  const resolveGroupId = useCallback(
    (label: LabelPickerLabel): string => {
      if (noLabelOption && label.id === SPECIAL_VALUES.noLabelsData.id) {
        return noLabelOption.selected ? labelsGroup.groupId : suggestionsGroup.groupId
      }

      const {selectedIds, copilotIds} = lookupSets
      // Only suggestions that made it into the capped group are grouped as
      // suggestions; anything beyond the cap renders as a normal label.
      const isSuggestedLabel = suggestionsById.has(label.id)

      if (selectedIds.has(label.id)) {
        return labelsGroup.groupId
      } else if (enableSuggestedLabels && isSuggestedLabel) {
        return suggestedGroup.groupId
      } else if (copilotIds.has(label.id)) {
        return copilotGroup.groupId
      }
      return suggestionsGroup.groupId
    },
    [noLabelOption, lookupSets, suggestionsById, enableSuggestedLabels],
  )

  // Emit a hydro event whenever a label is *added* from the open picker so we can
  // answer: do users pick a shown suggestion directly, or do they type a search
  // term first and then select? `hadSearchTerm` is the primary signal; `reason`
  // and `rank` carry the server's own attribution so client selections can be
  // joined against server-side ranking. Deselections are tracked in the ref but
  // not reported. Fires on pointer/Enter activation via the item's `onAction`;
  // the base picker's space-key toggle path is not instrumented.
  const emitLabelSelectionAnalytics = useCallback(
    (label: LabelPickerLabel) => {
      if (liveSelectedIdsRef.current.has(label.id)) {
        liveSelectedIdsRef.current.delete(label.id)
        return
      }
      liveSelectedIdsRef.current.add(label.id)

      const trimmedFilter = filterRef.current.trim()
      const reason = resolveSuggestionReason(label, {
        suggestionsById,
        copilotSuggestedLabels,
      })

      sendEvent('item-picker-label-suggestion-select', {
        hadSearchTerm: trimmedFilter.length > 0,
        searchTermLength: trimmedFilter.length,
        reason,
        // 0-based rank within the reason's group, only present for labels that
        // were actually surfaced as suggestions.
        rank: suggestionsById.get(label.id)?.rank,
        enableSuggestedLabels,
      })
    },
    [copilotSuggestedLabels, enableSuggestedLabels, suggestionsById],
  )

  const convertToItemProps = useCallback(
    (label: LabelPickerLabel): ExtendedItemProps<LabelPickerLabel> => {
      if (noLabelOption && label.id === SPECIAL_VALUES.noLabelsData.id) {
        const noLabelItem = {...noLabelOption}
        noLabelItem.groupId = noLabelItem.selected ? labelsGroup.groupId : suggestionsGroup.groupId
        return noLabelItem
      }

      if (label.id === filter) {
        return createLabelActionItemProps({
          label,
          inlineLabelCreationEnabled,
          repositoryId,
          openCreateLabelForm,
          createNewLabel,
        })
      }

      const groupId = resolveGroupId(label)
      // Only labels rendered in the server-driven suggestions group carry a
      // reason subtext, and only for reasons this client knows how to name.
      const reasonText =
        groupId === suggestedGroup.groupId
          ? SUGGESTION_REASON_TEXT[suggestionsById.get(label.id)?.reason ?? '']
          : undefined

      const isArchived = isLabelArchived(label)

      return {
        id: label.id,
        description: label.description ?? undefined,
        descriptionVariant: 'block',
        children: isArchived ? (
          <span className={styles.archivedLabelContent}>
            <ArchiveIcon size={12} className={styles.archivedLabelIcon} aria-label="Archived" />
            <VerifiedHTMLText
              // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point.
              html={label.nameHTML as SafeHTMLString}
              className={styles.labelNameText}
            />
          </span>
        ) : (
          <VerifiedHTMLText
            // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point.
            html={label.nameHTML as SafeHTMLString}
            className={styles.labelNameText}
          />
        ),
        source: label,
        groupId,
        onAction: () => emitLabelSelectionAnalytics(label),
        trailingVisual: reasonText ? () => <span className={styles.reasonText}>{reasonText}</span> : undefined,
        leadingVisual: showColors
          ? () => (
              <div
                style={{
                  background: `#${label.color}`,
                  borderColor: `#${label.color}`,
                }}
                className={styles.labelColorBox}
              />
            )
          : undefined,
      }
    },
    [
      noLabelOption,
      filter,
      suggestionsById,
      resolveGroupId,
      inlineLabelCreationEnabled,
      repositoryId,
      openCreateLabelForm,
      createNewLabel,
      showColors,
      emitLabelSelectionAnalytics,
    ],
  )

  const groups: ItemGroup[] = useMemo(() => {
    // Primer renders every supplied group heading, even when no rendered item
    // belongs to it. Item construction can drop labels (e.g. every suggestion is
    // already selected) and a suggestions-only response has nothing left for the
    // normal list, so deriving headings from the raw suggestion list leaves empty
    // sections. Instead, drive the headings off the group ids the rendered items
    // actually resolve to.
    const presentGroupIds = new Set(items.map(item => resolveGroupId(item)))
    const itemGroups: ItemGroup[] = []

    if (presentGroupIds.has(labelsGroup.groupId)) {
      itemGroups.push(enableSuggestedLabels ? selectedGroup : labelsGroup)
    }

    if (enableSuggestedLabels && presentGroupIds.has(suggestedGroup.groupId)) {
      // A single server-ranked "Suggestions" group, directly above the normal list.
      itemGroups.push(suggestedGroup)
    }

    if (presentGroupIds.has(copilotGroup.groupId)) {
      itemGroups.push(copilotGroup)
    }

    if (enableSuggestedLabels) {
      // Suggestions experience: the normal list is relabeled "Labels" and only
      // rendered when items actually resolve to it, so no empty heading shows.
      if (presentGroupIds.has(suggestionsGroup.groupId)) {
        itemGroups.push(labelsListGroup)
      }
    } else if (presentGroupIds.has(suggestionsGroup.groupId) || presentGroupIds.has(copilotGroup.groupId)) {
      // Feature disabled: the "Suggestions" list accompanies any Copilot group.
      // The picker only draws group headings when more than one group is
      // present, so without this companion a lone Copilot group (every
      // unselected label is a Copilot suggestion) would render its items flat
      // with no heading.
      itemGroups.push(suggestionsGroup)
    }
    return itemGroups
  }, [items, resolveGroupId, enableSuggestedLabels])

  const editLabelsPath = useMemo(() => {
    if (!repoNwo || !ssrSafeDocument?.location) return undefined
    const baseUrl = ssrSafeDocument.location.origin
    return `${baseUrl}/${repoNwo}${URLS.labelPageSuffix}`
  }, [repoNwo])

  const onEditLabels = useCallback(
    (e: React.MouseEvent) => {
      if (ssrSafeDocument?.location?.href && showEditLabelsButton && editLabelsPath) {
        // if the mouse wheel is clicked, do nothing
        if (e.button === 1) return

        // if a keyboard modifier is used, do nothing as the underlying default logic will
        // already open this into a new tab.
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return

        e.preventDefault()
        ssrSafeDocument.location.href = editLabelsPath
      }
    },
    [editLabelsPath, showEditLabelsButton],
  )

  const abandonCreateLabelFlow = useCallback(() => {
    const entryPoint = activeCreateLabelEntryPointRef.current
    if (!entryPoint) return

    activeCreateLabelEntryPointRef.current = null
    emitCreateLabelAnalytics('abandoned', entryPoint)
  }, [emitCreateLabelAnalytics])

  const completeCreateLabelFlow = useCallback(() => {
    const entryPoint = activeCreateLabelEntryPointRef.current
    if (!entryPoint) return

    activeCreateLabelEntryPointRef.current = null
    emitCreateLabelAnalytics('completed', entryPoint)
  }, [emitCreateLabelAnalytics])

  const abandonCreateLabelFlowOnUnmount = useEffectEvent(abandonCreateLabelFlow)

  useEffect(() => () => abandonCreateLabelFlowOnUnmount(), [])

  useEffect(() => {
    if (!inlineLabelCreationEnabled) abandonCreateLabelFlow()
  }, [abandonCreateLabelFlow, inlineLabelCreationEnabled])

  const closeCreateLabelForm = useCallback(() => {
    if (isCreateLabelPending) return

    abandonCreateLabelFlow()
    setIsCreateLabelPending(false)
    setIsCreateLabelFormOpen(false)
    requestAnimationFrame(() => createLabelReturnFocusRef.current?.focus())
  }, [abandonCreateLabelFlow, isCreateLabelPending])

  const handleBasePickerClose = useCallback(() => {
    abandonCreateLabelFlow()
    setIsCreateLabelPending(false)
    setIsCreateLabelFormOpen(false)
    setCreatedLabelsForSelection([])
    onPickerClose?.()
  }, [abandonCreateLabelFlow, onPickerClose])

  const handleLabelCreated = useCallback(
    (label: InlineLabelFormLabel) => {
      const createdLabel = {...label, __isNew__: true}
      setLoadedLabels(currentLabels => uniqById([createdLabel, ...currentLabels]))
      setCreatedLabels(currentLabels => uniqById([createdLabel, ...currentLabels]))
      setCreatedLabelsForSelection(currentLabels => uniqById([createdLabel, ...currentLabels]))
      onLabelCreated?.(label)
      announce(LABELS.createdAndSelectedLabel(label.name))
      completeCreateLabelFlow()
      setIsCreateLabelPending(false)
      setIsCreateLabelFormOpen(false)
      requestAnimationFrame(() => createLabelReturnFocusRef.current?.focus())
    },
    [completeCreateLabelFlow, onLabelCreated],
  )

  const handleSelectionChange = useCallback(
    (labels: LabelPickerLabel[]) => {
      const selectedRealLabels = labels.filter(label => !('__isCreateAction__' in label))
      const selectionChanged =
        selectedRealLabels.length !== selectedLabels.length ||
        selectedRealLabels.some(label => !selectedLabels.some(selectedLabel => selectedLabel.id === label.id))

      if (selectionChanged) onSelectionChange(selectedRealLabels)
    },
    [onSelectionChange, selectedLabels],
  )

  const showCreateLabelItem = showNoMatchItem && canCreateLabel && !isServerFetching && Boolean(filter)
  const showNoMatchMessage = useMemo(() => {
    const noPickerItems = !isServerFetching && !filter && items.length === 0
    const noFilterResults = !showCreateLabelItem && !isServerFetching && !!filter && items.length === 0
    return noPickerItems || noFilterResults
  }, [isServerFetching, filter, items.length, showCreateLabelItem])
  const showCreateLabelForm = inlineLabelCreationEnabled && isCreateLabelFormOpen
  const pickerItems = showCreateLabelForm ? [] : items
  const createLabelActionItem: CreateLabelActionItem | undefined = showCreateLabelItem
    ? {
        id: filter,
        color: 'aaaaaa',
        name: filter,
        nameHTML: filter,
        description: undefined,
        url: '',
        __isCreateAction__: true,
      }
    : undefined

  return (
    <div className={styles.labelPickerContainer}>
      <ItemPicker
        loading={removeLabelsLoadingEnabled ? isLoading : isLoading || isServerFetching}
        items={pickerItems}
        initialSelectedItems={selectedLabels}
        title={showCreateLabelForm ? LABELS.createLabelFormTitle : title || LABELS.labelsHeader}
        filterItems={filterItems}
        getItemKey={getItemKey}
        convertToItemProps={convertToItemProps}
        placeholderText={LABELS.filterLabels}
        selectionVariant={pickerItems.length === 0 ? 'single' : 'multiple'}
        preventClose={showCreateLabelForm && isCreateLabelPending}
        onSelectionChange={handleSelectionChange}
        onOpen={onPickerOpen}
        onClose={handleBasePickerClose}
        renderAnchor={props => anchorElement(props, anchorRef)}
        groups={!filter ? groups : undefined}
        insidePortal={insidePortal}
        height={enableSuggestedLabels ? 'xlarge' : 'large'}
        width={'medium'}
        nested={nested}
        resultListAriaLabel={'Label results'}
        triggerOpen={triggerOpen}
        initialFilter={initialFilter}
        inputRef={filterInputRef}
        selectPanelRef={anchorRef}
        keybindingCommandId="item-pickers:open-labels"
        eventsTag="label"
        anchorSide={anchorSide}
        customNoMatchItem={createLabelActionItem}
        suppressNoMatchItem
        improvedNoMatchAccessibility={showCreateLabelForm || showNoMatchMessage}
        noMatchMessage={
          showCreateLabelForm
            ? {
                title: LABELS.createLabelFormTitle,
                body: '',
                variant: 'empty',
                action: (
                  <>
                    <IconButton
                      aria-label={LABELS.backToLabels}
                      aria-disabled={isCreateLabelPending}
                      icon={ArrowLeftIcon}
                      onClick={isCreateLabelPending ? undefined : closeCreateLabelForm}
                      size="small"
                      variant="invisible"
                      data-component="CreateLabelBackButton"
                    />
                    <InlineLabelForm
                      repository={{id: repositoryId ?? ''}}
                      initialName={createLabelInitialName}
                      onCancel={closeCreateLabelForm}
                      onPendingChange={setIsCreateLabelPending}
                      onSuccess={handleLabelCreated}
                      variant={InlineLabelFormVariant.Picker}
                    />
                  </>
                ),
              }
            : showNoMatchMessage
              ? {
                  title: LABELS.noLabelsFound,
                  body: LABELS.noLabelsFoundDescription,
                  variant: 'empty',
                }
              : undefined
        }
        className={showCreateLabelForm ? styles.inlineCreateList : undefined}
        secondaryAction={
          !showCreateLabelForm && showEditLabelsButton ? (
            <SelectPanel.SecondaryActionLink onClick={onEditLabels} href={editLabelsPath} size={'small'}>
              {LABELS.editLabels}
            </SelectPanel.SecondaryActionLink>
          ) : undefined
        }
        responsiveOnNarrow={responsiveOnNarrowEnabled}
        focusPrependedElements
      />
    </div>
  )
}

const uniqById = (arr: LabelPickerLabel[]) => {
  return arr.filter((i, idx, a) => a.findIndex(item => item.id === i.id) === idx)
}
